import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowRight, Clock } from "lucide-react";
import { API, IMG_LUMBA, imgSrc, imageRatioClass } from "@/lib/api";
import { usePageMeta } from "@/lib/seo";

export default function Blog() {
  usePageMeta("Panduan Tali, Katrol & Timbangan", "Panduan lengkap seputar tali tambang PP & PE, katrol LUMBA LUMBA, dan timbangan dari NAWADAH by NDH Official.");
  const [blogs, setBlogs] = useState(null);
  const [site, setSite] = useState(null);

  useEffect(() => {
    API.get("/blog").then((r) => setBlogs(r.data)).catch(() => setBlogs([]));
    API.get("/settings").then((r) => setSite(r.data)).catch(() => {});
  }, []);

  const layout = site?.blog_layout || "grid";
  const list = blogs || [];

  const meta = (b) => (
    <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-gold mb-2 flex items-center gap-2">
      <Clock className="h-3.5 w-3.5" /> {b.read_time || "5 menit baca"} • {b.date_label}
    </p>
  );

  const gridCard = (b) => (
    <Link
      key={b.slug}
      to={`/blog/${b.slug}`}
      data-testid={`blog-card-${b.slug}`}
      className="card-maritime-surface rounded-md overflow-hidden hover:-translate-y-1 hover:border-gold/50 transition-transform group"
    >
      <div className={`${imageRatioClass(b.image_ratio, "16:9")} overflow-hidden bg-white`}>
        <img src={b.image ? imgSrc(b.image) : IMG_LUMBA} alt={b.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
      </div>
      <div className="p-6">
        {meta(b)}
        <h2 className="font-bold text-base mb-2 leading-snug">{b.title}</h2>
        <p className="text-sm text-slate-400 leading-relaxed line-clamp-2 mb-4">{b.excerpt}</p>
        <span className="inline-flex items-center gap-1 text-sm font-semibold text-gold">
          Baca Selengkapnya <ArrowRight className="h-4 w-4" />
        </span>
      </div>
    </Link>
  );

  const listCard = (b) => (
    <Link
      key={b.slug}
      to={`/blog/${b.slug}`}
      data-testid={`blog-card-${b.slug}`}
      className="card-maritime-surface rounded-md overflow-hidden hover:border-gold/50 transition-colors group flex flex-col sm:flex-row"
    >
      <div className={`sm:w-64 shrink-0 overflow-hidden bg-white ${imageRatioClass(b.image_ratio, "16:9")}`}>
        <img src={b.image ? imgSrc(b.image) : IMG_LUMBA} alt={b.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
      </div>
      <div className="p-6 flex-1">
        {meta(b)}
        <h2 className="font-bold text-lg mb-2 leading-snug">{b.title}</h2>
        <p className="text-sm text-slate-400 leading-relaxed line-clamp-3 mb-4">{b.excerpt}</p>
        <span className="inline-flex items-center gap-1 text-sm font-semibold text-gold">
          Baca Selengkapnya <ArrowRight className="h-4 w-4" />
        </span>
      </div>
    </Link>
  );

  const bigCard = (b) => (
    <Link
      key={b.slug}
      to={`/blog/${b.slug}`}
      data-testid={`blog-card-${b.slug}`}
      className="card-maritime-surface rounded-md overflow-hidden hover:border-gold/50 transition-colors group grid grid-cols-1 md:grid-cols-2"
    >
      <div className={`overflow-hidden bg-white self-start w-full ${imageRatioClass(b.image_ratio, "16:9")}`}>
        <img src={b.image ? imgSrc(b.image) : IMG_LUMBA} alt={b.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
      </div>
      <div className="p-8 flex flex-col justify-center">
        {meta(b)}
        <h2 className="font-display text-2xl mb-3 leading-snug">{b.title}</h2>
        <p className="text-sm text-slate-400 leading-relaxed line-clamp-3 mb-5">{b.excerpt}</p>
        <span className="inline-flex items-center gap-1 text-sm font-semibold text-gold">
          Baca Selengkapnya <ArrowRight className="h-4 w-4" />
        </span>
      </div>
    </Link>
  );

  return (
    <div className="bg-navy-dark text-white min-h-screen">
      <section className="relative py-20 border-b border-gold/20 bg-maritime-grid noise-overlay" data-testid="blog-header">
        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse at 15% 0%, rgba(201,162,39,0.12) 0%, transparent 55%)" }} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-gold mb-3">Blog &amp; Artikel</p>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tight">Panduan Produk NAWADAH</h1>
          <p className="mt-4 text-slate-400 text-sm sm:text-base max-w-2xl">
            Artikel lengkap seputar tali tambang, katrol, dan timbangan — membantu Anda memilih produk yang tepat.
          </p>
        </div>
      </section>

      <section className="relative py-16 bg-rope-pattern">
        <div className="max-w-7xl mx-auto px-4 sm:px-6" data-testid="blog-grid">
          {layout === "list" && <div className="space-y-5">{list.map(listCard)}</div>}
          {layout === "unggulan" && (
            <div>
              {list[0] && <div className="mb-6">{bigCard(list[0])}</div>}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">{list.slice(1).map(gridCard)}</div>
            </div>
          )}
          {layout === "grid" && <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">{list.map(gridCard)}</div>}
          {blogs && blogs.length === 0 && <p className="text-slate-400 text-sm">Belum ada artikel yang dipublish.</p>}
        </div>
      </section>
    </div>
  );
}
